import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, apiUtils } from '../service/api';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

const normalizeUserProfile = (rawUser) => {
  if (!rawUser) return null;
  const backendName = (rawUser.name || '').trim();

  return {
    ...rawUser,
    name: backendName || '', 
  };
};

const prepareProfilePayload = (profileData = {}) => {
  const payload = { ...profileData };

  if (payload.name !== undefined) {
    payload.name = payload.name ? payload.name.trim() : '';
  }
  delete payload.mobile;
  delete payload.cust_id;
  delete payload.country_code;
  delete payload.is_profile_complete;
  delete payload.created_at;
  delete payload.last_login_at;

  return payload;
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  // Simple state management with useState
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // FIX 1: Initial loading state must be TRUE to block routing until check is complete
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('my_access_token');
    const refreshToken = localStorage.getItem('my_refresh_token');
    const userData = localStorage.getItem('user_data');

    console.log("token")

    
    let authenticated = false;
    let currentUser = null;

    // if (token && refreshToken && userData) {
    //   try {
    //     const parsedUser = JSON.parse(userData);
    //     const normalizedUser = normalizeUserProfile(parsedUser.user || parsedUser);

    //     if (normalizedUser) {
    //       currentUser = normalizedUser;
    //       authenticated = true;
    //     } else {
          
    //       localStorage.removeItem('user_data');
    //       localStorage.removeItem('my_access_token');
    //       localStorage.removeItem('my_refresh_token');
    //     }
    //   } catch (error) {
    //     console.error('Error parsing user data:', error);
        
    //     localStorage.removeItem('my_access_token');
    //     localStorage.removeItem('my_refresh_token');
    //     localStorage.removeItem('user_data');
    //   }
    // }


    setUser(currentUser);
    setIsAuthenticated(authenticated);


    setLoading(false);
  }, []);


  // Send OTP
  const sendOTP = async (data) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.sendOTP(data);
      console.log("Send otp response", response);

      if (response.data.status === 'success') {
        setLoading(false);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      } else {
        setError(response.data.message);
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorData = apiUtils.handleError(error);
      setError(errorData.message);
      setLoading(false);
      return {
        success: false,
        message: errorData.message,
      };
    }
  };

  // Resend OTP
  const resendOTP = async (data) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.resendOTP(data);

      if (response.data.success) {
        setLoading(false);
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      } else {
        setError(response.data.message);
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorData = apiUtils.handleError(error);
      setError(errorData.message);
      setLoading(false);
      return {
        success: false,
        message: errorData.message,
      };
    }
  };

  // Verify OTP
  const verifyOTP = async (data) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.verifyOTP(data);

      console.log("verify otp", response);

      if (response.data.status === 'success') {
        const userData = normalizeUserProfile(response.data.data.customer);
        const tokens = response.data.data;
        const accessToken = tokens?.access_token;
        const refreshToken = tokens?.refresh_token;

        console.log('🔐 Auth Response:', response.data);
        console.log('🔑 Tokens received:', tokens);
        console.log('🎫 Access Token to store:', accessToken);
        console.log('🔄 Refresh Token to store:', refreshToken);

        // Store both tokens in localStorage
        localStorage.setItem('my_access_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('my_refresh_token', refreshToken);
        }
        localStorage.setItem('user_data', JSON.stringify(userData));

        // Update state
        setUser(userData);
        setIsAuthenticated(true);
        setLoading(false); // Success: set loading false

        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      } else {
        setError(response.data.message);
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorData = apiUtils.handleError(error);
      setError(errorData.message);
      setLoading(false);
      return {
        success: false,
        message: errorData.message,
      };
    }
  };

  // Logout
  const logout = async ({ revokeAllDevices = false } = {}) => {
    const refreshToken = localStorage.getItem('my_refresh_token');
    try {
      if (refreshToken) {
        await authAPI.logout({
          refresh_token: refreshToken,
          revoke_all_devices: revokeAllDevices,
        });
      }
    } catch (logoutError) {
      console.error('Logout request failed:', logoutError);
    } finally {
      localStorage.removeItem('my_access_token');
      localStorage.removeItem('my_refresh_token');
      localStorage.removeItem('user_data');
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Refresh access token function
  const refreshAccessToken = (newAccessToken) => {
    localStorage.setItem('my_access_token', newAccessToken);
    console.log('✅ Access token updated in AuthContext');
  };

  // Integrate token refresh hook
  useTokenRefresh(isAuthenticated, refreshAccessToken);

  // Get user profile
  const getProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.getProfile();

      console.log("profile data in auth context", response);

      if (response.data.status === 'success') {
        const userData = normalizeUserProfile(response.data.data?.customer || response.data.data);
        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);
        setLoading(false);
        return {
          success: true,
          data: userData,
        };
      } else {
        setError(response.data.message);
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorData = apiUtils.handleError(error);
      setError(errorData.message);
      setLoading(false);
      return {
        success: false,
        message: errorData.message,
      };
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.updateProfile(prepareProfilePayload(profileData));

      if (response.data.status === 'success') {
        const userData = normalizeUserProfile(response.data.data?.customer || response.data.data);
        localStorage.setItem('user_data', JSON.stringify(userData));
        setUser(userData);
        setLoading(false);
        return {
          success: true,
          data: userData,
          message: response.data.message,
        };
      } else {
        setError(response.data.message);
        setLoading(false);
        return {
          success: false,
          message: response.data.message,
        };
      }
    } catch (error) {
      const errorData = apiUtils.handleError(error);
      setError(errorData.message);
      setLoading(false);
      return {
        success: false,
        message: errorData.message,
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    sendOTP,
    resendOTP,
    verifyOTP,
    logout,
    clearError,
    getProfile,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  // console.log("context", context);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;